/**
 * FrontRow Auth (Clerk-backed, clean MVP 2026-08-22).
 *
 * Clerk owns the full account UX (Register → verify email → Sign in →
 * Forgot password → Sign out → persistent session). This provider
 * exposes only the FrontRow-side household state:
 *
 *   status ∈ "checking" | "signed_out" | "verify_error" | "linked"
 *   familyId — the household bound to the signed-in Clerk user
 *
 * Identity model:
 *   Clerk `external_clerk_id` (sub) is the sole authenticated
 *   identity key. Login email is account/contact metadata only.
 *   The school-inbox email connected later in onboarding is a
 *   completely separate concept.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useRef, useState,
} from "react";
import { useAuth as useClerkAuth, useUser } from "@clerk/clerk-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthCtx = createContext(null);
const CLERK_SIGNED_OUT_DEBOUNCE_MS = 900;
// Short backoff (~10s) for checks that never got a server answer.
export const VERIFY_RETRY_DELAYS_MS = [1000, 2000, 3000, 4000];
const TRANSIENT_HTTP_STATUS = new Set([502, 503, 504]);
const isTransientVerifyError = (err) =>
  !err?.response || TRANSIENT_HTTP_STATUS.has(err.response.status);

export const AuthProvider = ({ children }) => {
  const { isLoaded, isSignedIn, getToken } = useClerkAuth();
  const { user } = useUser();
  const [status, setStatus] = useState("checking");
  const [familyId, setFamilyId] = useState(null);
  // Transient flag: true ONLY while a Clerk-signed-in caller is
  // waiting on POST /auth/verify to resolve. Used by <AuthedRoutes>
  // to render a hidden placeholder during that window — otherwise
  // the app briefly routes to /sign-in and remounts the auth shell
  // (visible flash between Clerk's own verification step and the
  // linked dashboard). MUST be reset in BOTH the success and
  // failure paths so a failed /auth/verify cannot leave the UI
  // blank indefinitely.
  const [verifyInFlight, setVerifyInFlight] = useState(false);
  // Monotonic invocation counter (2026-09-01 sign-in bounce fix).
  // The verify effect legitimately re-runs when Clerk swaps the
  // `getToken` identity during session activation. Only the LATEST
  // invocation may write state — a superseded invocation's failure
  // must never drop the loading gate or bounce an already-
  // authenticated user back to the Clerk password screen.
  const verifySeqRef = useRef(0);
  // 2026-09-01 factor-one bounce latch: during Clerk's setActive
  // session handover, useAuth can emit a transient isSignedIn=false
  // frame. Once this browser session has been seen signed-in, such a
  // frame must NOT drop the loading gate back to the sign-in shell.
  const everSignedInRef = useRef(false);
  const statusRef = useRef("checking");
  const signedOutTimerRef = useRef(null);
  statusRef.current = status;

  const cancelSignedOutTimer = useCallback(() => {
    if (signedOutTimerRef.current) {
      clearTimeout(signedOutTimerRef.current);
      signedOutTimerRef.current = null;
    }
  }, []);

  const retryTimerRef = useRef(null);
  const retryIndexRef = useRef(0);
  const attemptSeqRef = useRef(0);
  const transientFailureRef = useRef(false);
  const clearRetry = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  // One household check: "linked" | "waiting" | "transient" | "refused" | "stale".
  const verifyOnce = useCallback(async (isCurrent) => {
    const link = (r) => {
      if (!isCurrent()) return "stale";
      setFamilyId(r.data?.family_id || null);
      setStatus("linked");
      setVerifyInFlight(false);
      return "linked";
    };
    let token;
    // A token refresh that throws (offline on resume) is recoverable.
    try { token = await getToken(); } catch { return isCurrent() ? "transient" : "stale"; }
    if (!isCurrent()) return "stale";
    // Signed in but token still activating: keep the gate up; Clerk re-renders
    // with a new getToken identity, which re-runs verification.
    if (!token) return "waiting";
    try {
      return link(await postVerify(token));
    } catch (err) {
      if (!isCurrent()) return "stale";
      if (isTransientVerifyError(err)) return "transient";
    }
    // One fresh-token retry: a token minted mid-activation can be rejected once.
    let fresh;
    try { fresh = await getToken({ skipCache: true }); } catch { return isCurrent() ? "transient" : "stale"; }
    if (!isCurrent()) return "stale";
    if (!fresh) return "waiting";
    try {
      return link(await postVerify(fresh));
    } catch (err) {
      if (!isCurrent()) return "stale";
      return isTransientVerifyError(err) ? "transient" : "refused";
    }
  }, [getToken]);

  const attemptVerify = useCallback(async (seq) => {
    const isCurrent = () => seq === verifySeqRef.current;
    attemptSeqRef.current = seq;
    let outcome;
    try {
      outcome = await verifyOnce(isCurrent);
    } finally {
      if (attemptSeqRef.current === seq) attemptSeqRef.current = 0;
    }
    if (!isCurrent()) return;
    if (outcome === "transient" && retryIndexRef.current < VERIFY_RETRY_DELAYS_MS.length) {
      const delay = VERIFY_RETRY_DELAYS_MS[retryIndexRef.current];
      retryIndexRef.current += 1;
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        if (isCurrent()) attemptVerify(seq);
      }, delay);
      return;
    }
    if (outcome === "transient" || outcome === "refused") {
      // Clerk still owns an active session. Never remount <SignIn> after a
      // FrontRow verification failure (it can replay email verification);
      // hold a retryable application error instead.
      transientFailureRef.current = outcome === "transient";
      setStatus("verify_error");
      setFamilyId(null);
      setVerifyInFlight(false);
    }
  }, [verifyOnce]);

  const runVerify = useCallback(async () => {
    const seq = ++verifySeqRef.current;
    clearRetry();
    retryIndexRef.current = 0;
    transientFailureRef.current = false;
    if (!isLoaded) { setStatus("checking"); return; }
    if (!isSignedIn) {
      // Clerk can briefly emit isSignedIn=false during setActive/session
      // handover even after FrontRow has already linked the household.
      // Previously that single frame immediately remounted the Clerk
      // sign-in shell, which could replay email verification and issue a
      // second code. Debounce the transition instead. A genuine sign-out
      // remains false beyond this short window and is handled normally.
      if (everSignedInRef.current) {
        cancelSignedOutTimer();
        signedOutTimerRef.current = setTimeout(() => {
          if (seq !== verifySeqRef.current) return;
          setStatus("signed_out");
          setFamilyId(null);
          setVerifyInFlight(false);
          signedOutTimerRef.current = null;
        }, CLERK_SIGNED_OUT_DEBOUNCE_MS);
        return;
      }
      setStatus("signed_out");
      setFamilyId(null);
      setVerifyInFlight(false);
      return;
    }

    cancelSignedOutTimer();
    everSignedInRef.current = true;
    setVerifyInFlight(true);
    await attemptVerify(seq);
  }, [isLoaded, isSignedIn, cancelSignedOutTimer, clearRetry, attemptVerify]);

  useEffect(() => { runVerify(); }, [runVerify]);

  // Back online or back in the foreground: retry a pending or network-failed
  // check now. Never overlaps an attempt already in flight.
  useEffect(() => {
    const resume = () => {
      if (document.visibilityState === "hidden") return;
      const seq = verifySeqRef.current;
      if (attemptSeqRef.current === seq) return;
      if (retryTimerRef.current) {
        clearRetry();
        attemptVerify(seq);
      } else if (statusRef.current === "verify_error" && transientFailureRef.current) {
        runVerify();
      }
    };
    window.addEventListener("online", resume);
    document.addEventListener("visibilitychange", resume);
    return () => {
      window.removeEventListener("online", resume);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [attemptVerify, clearRetry, runVerify]);

  useEffect(() => clearRetry, [clearRetry]);

  useEffect(() => () => cancelSignedOutTimer(), [cancelSignedOutTimer]);

  // Global axios interceptor — attach Clerk short-lived JWT on every
  // request. Backend middleware verifies it via JWKS and resolves
  // `family_id` from `ee_users.external_clerk_id`. Cleared on sign-out.
  useEffect(() => {
    const id = axios.interceptors.request.use(async (cfg) => {
      if (isSignedIn && getToken) {
        try {
          const t = await getToken();
          if (t) cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${t}` };
        } catch { /* leave request unauthenticated */ }
      }
      cfg.withCredentials = window.location.protocol !== "capacitor:";
      return cfg;
    });
    return () => axios.interceptors.request.eject(id);
  }, [isSignedIn, getToken]);

  return (
    <AuthCtx.Provider value={{ status, familyId, verifyInFlight,
                                clerkUser: user, refresh: runVerify }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
};

function postVerify(token) {
  return axios.post(
    `${API}/auth/verify`, {},
    { headers: { Authorization: `Bearer ${token}` },
      withCredentials: window.location.protocol !== "capacitor:" },
  );
}
